// 🌿 API Craft Back Office - Login Page
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle, PAGE_TITLES } from '../hooks/usePageTitle';
import { useNavigate, Link } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Set page title
  usePageTitle(PAGE_TITLES.LOGIN);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // Login handles redirect automatically
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mint-50 via-sage-50 to-emerald-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-mint-200/30 to-emerald-300/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-sage-200/30 to-mint-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center animation-slide-down">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-r from-mint-400 to-emerald-500 shadow-strong mb-6">
            <span className="text-3xl text-white">🌿</span>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-forest-700 to-emerald-600 bg-clip-text text-transparent">
            Welcome to API Craft
          </h2>
          <p className="mt-2 text-sage-600">
            Sign in to manage your dynamic APIs and models
          </p>
          <div className="mt-4 p-3 bg-mint-50 border border-mint-200 rounded-lg">
            <p className="text-xs text-forest-600 text-center">
Enter your API Craft credentials
            </p>
          </div>
        </div>
        
        {/* Login Form */}
        <form className="card-glass space-y-6 animation-slide-up" onSubmit={handleSubmit}>
          {error && (
            <div className="alert-error animation-scale-in">
              <div className="flex items-center">
                <span className="text-red-500 mr-3 text-xl">⚠️</span>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-forest-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="Enter your email address"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-forest-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="mr-2">🚀</span>
                  Sign in to API Craft
                </span>
              )}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-sage-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-mint-600 hover:text-emerald-600 transition-colors duration-200 hover:underline"
              >
                Create one here
              </Link>
            </span>
          </div>
        </form>

        {/* Features preview */}
        <div className="grid grid-cols-3 gap-4 pt-6 animation-fade-in">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto bg-gradient-to-r from-mint-100 to-emerald-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-lg">📊</span>
            </div>
            <p className="text-xs text-sage-600">Dynamic Models</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto bg-gradient-to-r from-sage-100 to-mint-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-lg">⚡</span>
            </div>
            <p className="text-xs text-sage-600">Instant APIs</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto bg-gradient-to-r from-emerald-100 to-mint-100 rounded-xl flex items-center justify-center mb-2">
              <span className="text-lg">🔐</span>
            </div>
            <p className="text-xs text-sage-600">Secure Access</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
